import { LessonGalleryItem } from "@/components/common/lesson-gallery/lesson-gallery";
import { getSeconds, secondsToTimeStr } from "@/utils/convertDuration";
import { GetterTree } from "vuex";
import { ContentRootType, LPItemMetadata, Exposure, ExposureItem, ExposureItemMedia, LessonState } from "./state";
import { ExposureDetailType } from "@/views/teacher-class/components/lesson-plan/lesson-plan";
import { defineVuexName, MODULES } from "@/store/utils";
const extractAllMedia = (s: LessonState): ExposureItemMedia[] => {
  if (!s.currentExposure) return [];
  const groupMedia = [];
  const isAlternateMediaShown = getSeconds(s.playedTime) === getSeconds(s.totalTime);
  const combinedItems = [
    ...s.currentExposure.items,
    ...s.currentExposure.contentBlockItems,
    ...s.currentExposure.teachingActivityBlockItems,
    ...(isAlternateMediaShown ? s.currentExposure.alternateMediaBlockItems.flat() : []),
  ].filter((item: ExposureItem) => item.media[0]?.image?.url);
  for (const item of combinedItems) {
    groupMedia.push(item.media);
  }
  return groupMedia.flat();
};
const getters: GetterTree<LessonState, any> = {
  //currentExposure
  [defineVuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE)](s: LessonState): Exposure | undefined {
    return s.currentExposure;
  },
  //nextExposure
  [defineVuexName(MODULES.LESSON.GETTERS.GET_NEXT_EXPOSURE)](s: LessonState): Exposure | undefined {
    return s.nextExposure;
  },
  //currentExposureItemMedia
  [defineVuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_ITEM_MEDIA)](s: LessonState): ExposureItemMedia | undefined {
    return s.currentExposureItemMedia;
  },
  //currentExposureType
  [defineVuexName(MODULES.LESSON.GETTERS.GET_CURRENT_EXPOSURE_TYPE)](s: LessonState): ExposureDetailType | undefined {
    return s.currentExposureType;
  },
  //nextExposureItemMedia
  [defineVuexName(MODULES.LESSON.GETTERS.GET_NEXT_EXPOSURE_ITEM_MEDIA)](s: LessonState): ExposureItemMedia | undefined {
    if (!s.currentExposure || !s.currentExposureItemMedia) return;
    const allMedia: ExposureItemMedia[] = extractAllMedia(s);
    const indexOfMedia =
      (s.currentExposureType
        ? allMedia.findIndex((media) => media.id === s.currentExposureItemMedia?.id && media.exposureType === s.currentExposureType)
        : 0) || allMedia.findIndex((media) => media.id === s.currentExposureItemMedia?.id);
    if (indexOfMedia + 1 < allMedia.length) {
      return (s.nextExposureItemMedia = allMedia[indexOfMedia + 1]);
    } else {
      return (s.nextExposureItemMedia = undefined);
    }
  },
  //prevExposureItemMedia
  [defineVuexName(MODULES.LESSON.GETTERS.GET_PREV_EXPOSURE_ITEM_MEDIA)](s: LessonState): ExposureItemMedia | undefined {
    if (!s.currentExposure || !s.currentExposureItemMedia) return;
    const allMedia: ExposureItemMedia[] = extractAllMedia(s);
    const indexOfMedia =
      (s.currentExposureType
        ? allMedia.findIndex((media) => media.id === s.currentExposureItemMedia?.id && media.exposureType === s.currentExposureType)
        : 0) || allMedia.findIndex((media) => media.id === s.currentExposureItemMedia?.id);
    if (indexOfMedia - 1 >= 0) {
      return (s.nextExposureItemMedia = allMedia[indexOfMedia - 1]);
    } else {
      return (s.nextExposureItemMedia = undefined);
    }
  },
  //exposures
  [defineVuexName(MODULES.LESSON.GETTERS.GET_EXPOSURES)](s: LessonState): Exposure[] {
    return s.exposures;
  },
  //activityStatistic
  [defineVuexName(MODULES.LESSON.GETTERS.GET_ACTIVITY_STATISTIC)](s: LessonState): string {
    // count only required exposure as total exposure
    const listExpo: (string | undefined)[] = [];
    const requiredExposures = s.exposures.filter((expo) => {
      const isRequiredExposure = expo.contentRootType !== ContentRootType.Optional && expo.contentRootType !== ContentRootType.Complete;

      if (isRequiredExposure) {
        listExpo.push(expo.id);
      }

      return isRequiredExposure;
    });

    return requiredExposures.length ? `${listExpo.indexOf(s.currentExposure?.id) + 1}/${requiredExposures.length}` : "0";
  },
  //getPage
  [defineVuexName(MODULES.LESSON.GETTERS.GET_PAGE)](s: LessonState): string {
    const listMedia: string[] = [];
    if (!s.currentExposure) {
      return "";
    }
    const isAlternateMediaShown = getSeconds(s.playedTime) === getSeconds(s.totalTime);
    const combinedItems = [
      ...s.currentExposure.items,
      ...s.currentExposure.contentBlockItems,
      ...s.currentExposure.teachingActivityBlockItems,
      ...(isAlternateMediaShown ? s.currentExposure.alternateMediaBlockItems.flat() : []),
    ].filter((item: ExposureItem) => item.media[0]?.image?.url);
    combinedItems.map((item) => {
      item.media.map((media) => {
        listMedia.push(media.id);
      });
    });
    const currentPage: string[] = [];
    if (listMedia.length > 0) {
      listMedia.map((media) => {
        if (media == s.currentExposureItemMedia?.id) {
          currentPage.push(media);
        }
      });
    }
    return s.exposures.length ? `${listMedia.indexOf(currentPage[0]) + 1}/${listMedia.length}` : "0";
  },
  //remainingTimeStatistic
  [defineVuexName(MODULES.LESSON.GETTERS.GET_REMAINING_TIME_STATISTIC)](s: LessonState): string {
    return secondsToTimeStr(getSeconds(s.totalTime) - getSeconds(s.playedTime));
  },
  //progressStatistic
  [defineVuexName(MODULES.LESSON.GETTERS.GET_PROGRESS_STATISTIC)](s: LessonState): number {
    return getSeconds(s.playedTime) / getSeconds(s.totalTime);
  },
  //isAlternateMediaShown
  [defineVuexName(MODULES.LESSON.GETTERS.GET_IS_ALTERNATE_MEDIA_SHOWN)](s: LessonState): boolean {
    return getSeconds(s.playedTime) === getSeconds(s.totalTime);
  },
  //previousExposure
  [defineVuexName(MODULES.LESSON.GETTERS.GET_PREVIOUS_EXPOSURE)](s: LessonState): Exposure | undefined {
    return s.previousExposure;
  },
  //previousExposureItemMedia
  [defineVuexName(MODULES.LESSON.GETTERS.GET_PREVIOUS_EXPOSURE_ITEM_MEDIA)](s: LessonState): ExposureItemMedia | undefined {
    return s.previousExposureItemMedia;
  },
  //findCachedImage
  [defineVuexName(MODULES.LESSON.GETTERS.GET_FIND_CACHED_IMAGE)](s: LessonState) {
    return function (imgData: { url: string; metadata: LPItemMetadata }): string | undefined {
      const cacheData = s.cropCache?.cacheValues.find(
        (cacheValue) => cacheValue.url === imgData.url && JSON.stringify(cacheValue.metadata) === JSON.stringify(imgData.metadata),
      );
      if (cacheData) {
        // founded, then return the base64 string
        return cacheData.base64String;
      }
      // not found
      return undefined;
    };
  },
  //zoomRatio
  [defineVuexName(MODULES.LESSON.GETTERS.GET_ZOOM_RATIO)](s: LessonState) {
    return s.zoomRatio;
  },
  //imgCoords
  [defineVuexName(MODULES.LESSON.GETTERS.GET_IMG_COORDS)](s: LessonState) {
    return s.imgCoords;
  },
  //previewObjects
  [defineVuexName(MODULES.LESSON.GETTERS.GET_PREVIEW_OBJECTS)](s: LessonState) {
    return s.previewObjects;
  },
  //isShowPreviewCanvas
  [defineVuexName(MODULES.LESSON.GETTERS.GET_IS_SHOW_PREVIEW_CANVAS)](s: LessonState) {
    return s.isShowPreviewCanvas;
  },
  //* IM mode's items
  //listReaderItemsOfCurrentExposure
  [defineVuexName(MODULES.LESSON.GETTERS.GET_LIST_READER_ITEMS_OF_CURRENT_EXPOSURE)](s: LessonState): LessonGalleryItem[] | null {
    if (!s.currentExposure?.includedIndependentMode) return null;
    return (
      s.currentExposure?.contentBlockItems
        .filter((item: ExposureItem) => item.media[0]?.image?.url && !item.media[0].teacherUseOnly)
        .map((item: ExposureItem) => {
          const { media } = item;
          const readerAsGalleryItem: LessonGalleryItem = {
            id: item.id,
            imageUrl: media[0].image.url,
          };
          if (media[0]?.audio) {
            readerAsGalleryItem.audioUrl = media[0].audio?.url;
          }
          if (media[0]?.readerFocusWord) {
            readerAsGalleryItem.focusWord = media[0].readerFocusWord;
          }
          return readerAsGalleryItem;
        }) ?? null
    );
  },
  //pdfScrollProgress
  [defineVuexName(MODULES.LESSON.GETTERS.GET_PDF_SCROLL_PROGRESS)](s: LessonState) {
    return s.pdfScrollProgress;
  },
  //alternativeShown
  [defineVuexName(MODULES.LESSON.GETTERS.GET_ALTERNATIVE_SHOWN)](s: LessonState): boolean {
    return getSeconds(s.playedTime) === getSeconds(s.totalTime);
  },
  [defineVuexName(MODULES.LESSON.GETTERS.GET_VISIBLE_TARGET_TAGS)](s: LessonState) {
    return s.visibleTargetTags;
  },
};

export default getters;
